import { Modal, Button, Form, Message, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";

// Custom Functions
import { emailValidate } from "../../lib/emailValidation";

// API
import { CheckTeams as API_CheckTeams } from "../../api/tenant";

// -----------------------------------------------------
// Stato del servizio Teams: verifica a comando (nessun polling).
// Balanced -> richiesta senza payload, il BE controlla le room configurate.
// Direct   -> chiede un indirizzo Microsoft abilitato a Teams.
// L'esito e' volatile: vive nel componente, non in redux.
// -----------------------------------------------------

function ServiceStatusBadge(props) {
  const { t } = useTranslation();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const [emailModal, setEmailModal] = useState(false);
  const [resultsModal, setResultsModal] = useState(false);
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState(null);

  // ---------

  const fncCheck = (room_email) => {
    setLoading(true);
    setFailed(false);
    API_CheckTeams(props.username, props.token, room_email)
      .then((body) => {
        setResults(body.data.results || []);
        setLoading(false);
        setEmailModal(false);
        setEmail("");
        setFormError(null);
      })
      .catch((error) => {
        setLoading(false);
        if (error.response !== undefined && error.response.status === 401) {
          props.onAuthError();
          return;
        }
        // Con il modale aperto l'errore resta nel form, altrimenti va nel badge
        if (room_email) {
          setFormError(t("PortalMessages.Error.Server"));
        } else {
          setFailed(true);
        }
      });
  };

  const fncRun = () => {
    if (props.mode === "direct") {
      setEmail("");
      setFormError(null);
      setEmailModal(true);
    } else {
      fncCheck();
    }
  };

  // ---------

  // -> { label, className, clickable }
  const fncStatus = () => {
    if (loading) {
      return {
        label: t("Status.Checking"),
        className: "tp_card_status_unactive",
        clickable: false,
      };
    }
    if (failed) {
      return {
        label: t("Status.CheckFailed"),
        className: "tp_card_status_error",
        clickable: false,
      };
    }
    // results vuoto = niente da controllare, non un guasto
    if (results === null || results.length === 0) {
      return {
        label: t("Status.NotChecked"),
        className: "tp_card_status_unactive",
        clickable: false,
      };
    }

    var ok = results.filter((r) => r.success === true).length;
    if (ok === results.length) {
      return {
        label: t("Status.Working"),
        className: "tp_card_status_active",
        clickable: true,
      };
    }
    if (ok === 0) {
      return {
        label: t("Status.NotWorking"),
        className: "tp_card_status_error",
        clickable: true,
      };
    }
    return {
      label: t("Status.PartiallyWorking", { ok: ok, total: results.length }),
      className: "tp_card_status_warning",
      clickable: true,
    };
  };

  const status = fncStatus();

  // Riepilogo in cima al modale: cosa e' andato storto e dove guardare
  const fncResultsSummary = () => {
    var rows = results || [];
    var ok = rows.filter((r) => r.success === true).length;
    var ko = rows.length - ok;

    if (ko === 0) {
      return <Message success content={t("Tenant.CheckTeamsAllOk")} />;
    }
    if (ok === 0) {
      return <Message error content={t("Tenant.CheckTeamsNoneOk")} />;
    }
    return (
      <Message
        warning
        content={t("Tenant.CheckTeamsPartial", { ko: ko, total: rows.length })}
      />
    );
  };

  // ---------

  return (
    <div className="tp_service_status">
      {status.clickable ? (
        <button
          type="button"
          title={t("Tenant.CheckTeamsResults")}
          className={"tp_card_status_action " + status.className}
          onClick={() => setResultsModal(true)}
        >
          <span>{status.label}</span>
          <Icon name="info circle" fitted />
        </button>
      ) : (
        <p className={status.className}>
          {loading ? <Icon name="spinner" loading fitted /> : null}
          <span>{status.label}</span>
        </p>
      )}

      <Button
        icon="refresh"
        circular
        size="mini"
        type="button"
        disabled={loading}
        title={t("Tenant.CheckTeamsRun")}
        onClick={() => fncRun()}
      />

      {/* Direct: indirizzo Microsoft su cui eseguire il controllo */}
      <Modal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        size="tiny"
      >
        <Modal.Header>{t("Tenant.CheckTeams")}</Modal.Header>
        <Modal.Content>
          {formError !== null ? <Message error content={formError} /> : null}
          <Message info content={t("Tenant.CheckTeamsMessage")} />
          <Form>
            <Form.Input
              label={t("Tenant.CheckTeamsEmail")}
              value={email}
              error={formError !== null}
              onChange={(e, d) => {
                setEmail(d.value);
                setFormError(null);
              }}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("Button.Cancel")}
            disabled={loading}
            onClick={() => {
              setEmailModal(false);
              setFormError(null);
            }}
          />
          <Button
            content={t("Tenant.CheckTeamsRun")}
            className="tp_btn"
            loading={loading}
            disabled={loading}
            onClick={() => {
              var value = email.trim();
              if (!value) {
                setFormError(t("PortalMessages.Error.FieldsEmpty"));
              } else if (!emailValidate(value)) {
                setFormError(t("PortalMessages.Error.MailNotValid"));
              } else {
                fncCheck(value);
              }
            }}
          />
        </Modal.Actions>
      </Modal>

      {/* Esito per singolo indirizzo */}
      <Modal
        open={resultsModal}
        onClose={() => setResultsModal(false)}
        size="tiny"
      >
        <Modal.Header>{t("Tenant.CheckTeamsResults")}</Modal.Header>
        <Modal.Content>
          {fncResultsSummary()}
          <div className="tp_check_results">
            {(results || []).map((row, index) => (
              <div className="tp_check_result" key={index}>
                <Icon
                  name={row.success === true ? "check circle" : "times circle"}
                  className={
                    row.success === true ? "tp_check_ok" : "tp_check_ko"
                  }
                />
                <p>{row.email}</p>
                <span
                  className={
                    row.success === true ? "tp_check_ok" : "tp_check_ko"
                  }
                >
                  {row.success === true
                    ? t("Status.Working")
                    : t("Status.NotWorking")}
                </span>
              </div>
            ))}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("Button.Close")}
            onClick={() => setResultsModal(false)}
          />
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default ServiceStatusBadge;
